import type { ComponentProps, CSSProperties } from "react";
import { Button } from "@/components/ui/button";
import { useStoredTheme } from "@/utils/theme-context";
import { cn } from "@/utils/cn";

const channels = (color: string): number[] | null => {
  const hex = color.match(/^#([\da-f]{6})$/i)?.[1];
  if (hex) return hex.match(/../g)!.map((value) => parseInt(value, 16) / 255);
  return null;
};
const luminance = (color: number[]) => color.reduce((sum, value, index) =>
  sum + (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
    * [0.2126, 0.7152, 0.0722][index], 0);
const contrast = (first: number[], second: number[]) => {
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// Keep the configured brand surface. Prefer its configured label when it is
// readable; otherwise select an existing semantic foreground/background token.
// Check the shared Button's 90% hover surface as well as its normal surface.
const labelToken = (theme: ReturnType<typeof useStoredTheme>, mode: "light" | "dark") => {
  const background = channels(theme.button.primary.backgroundColor[mode]);
  const page = channels(theme.general.backgroundColor[mode]);
  if (!background || !page) return "var(--primary-foreground)";
  const hover = background.map((value, index) => value * 0.9 + page[index] * 0.1);
  const candidates = [
    ["--primary-foreground", theme.button.primary.textColor[mode]],
    ["--foreground", theme.general.textColor[mode]],
    ["--background", theme.general.backgroundColor[mode]],
  ].map(([token, color]) => {
    const rgb = channels(color);
    return { token, ratio: rgb ? Math.min(contrast(rgb, background), contrast(rgb, hover)) : 0 };
  });
  const readable = candidates.find(({ ratio }) => ratio >= 4.5);
  const best = readable ?? candidates.reduce((a, b) => a.ratio >= b.ratio ? a : b);
  return `var(${best.token})`;
};

/** Projects-only correction; shared Button defaults and backend tokens stay intact. */
export default function ProjectCreateButton({ className, style, ...props }: ComponentProps<typeof Button>) {
  const theme = useStoredTheme();
  return <Button
    {...props}
    className={cn("text-[color:var(--project-action-label)] dark:text-[color:var(--project-action-label-dark)]", className)}
    style={{
      "--project-action-label": labelToken(theme, "light"),
      "--project-action-label-dark": labelToken(theme, "dark"),
      ...style,
    } as CSSProperties}
  />;
}
